import { Auth0Context, useAuth0 } from '@auth0/auth0-react';
import { setAuthContextOnBackendService } from 'BackendAuthHandler';
import BaseBackendService from 'BackendService';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

const submitTrackClickForm = (payload: {
  actionLogId?: string | null;
  clickedUrl: string;
  linkId?: string | null;
  linkURL?: string | null;
}) => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = '/api/emails/track-click';
  form.style.display = 'none';

  const appendField = (name: string, value?: string | null) => {
    if (value === undefined || value === null) return;
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  };

  appendField('actionLogId', payload.actionLogId || '');
  appendField('clickedUrl', payload.clickedUrl);
  appendField('linkId', payload.linkId || '');
  appendField('linkURL', payload.linkURL || '');

  document.body.appendChild(form);
  form.submit();
};

/**
 * Layout route wrapper that intercepts email tracking params BEFORE rendering child routes
 * This ensures the URL is cleaned before any route component renders
 * - Always tracks email clicks immediately (preprocessor-style), regardless of authentication
 * - Cleans URL after tracking, then lets normal navigation/auth flow handle login requirements
 */
export const EmailTrackingLayout: React.FC<{ isAdmin?: boolean }> = ({ isAdmin = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const hasProcessedUrl = useRef<string | null>(null);
  const [shouldBlockRender, setShouldBlockRender] = useState(false);

  // Synchronous check during render - intercepts BEFORE children render
  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const fromEmail = urlParams.get('fromEmail');
  const linkId = urlParams.get('linkId');
  const linkURL = urlParams.get('linkURL');

  // Create a unique key for this URL to track if we've processed it
  const currentUrlKey = `${location.pathname}${location.search}${location.hash}`;

  // Check if we have any tracking parameters
  const hasTrackingParams = fromEmail || linkId || linkURL;

  // Handle email tracking in useEffect to avoid calling navigate during render
  useEffect(() => {
    // If we have email tracking params (fromEmail, linkId, or linkURL) and haven't processed this URL yet
    if (hasTrackingParams && hasProcessedUrl.current !== currentUrlKey) {
      // Mark as processed immediately to prevent re-processing
      hasProcessedUrl.current = currentUrlKey;

      // Skip email tracking for impersonation route - it handles its own flow
      // Tracking here could interfere with the impersonation code verification
      if (location.pathname === '/impersonate') {
        // Just clean the URL without tracking
        urlParams.delete('fromEmail');
        urlParams.delete('linkId');
        urlParams.delete('linkURL');
        const newSearch = urlParams.toString();
        const newUrl = location.pathname + (newSearch ? '?' + newSearch : '') + location.hash;
        navigate(newUrl, { replace: true });
        setShouldBlockRender(false);
        return;
      }

      // Always track email clicks immediately (preprocessor-style), regardless of authentication
      // Block rendering until tracking completes and URL is cleaned
      setShouldBlockRender(true);

      // Clean the URL
      urlParams.delete('fromEmail');
      urlParams.delete('linkId');
      urlParams.delete('linkURL');
      const newSearch = urlParams.toString();
      const newUrl = location.pathname + (newSearch ? '?' + newSearch : '') + location.hash;

      // Track the click immediately - no auth required
      // CRITICAL: Must wait for tracking request to complete (success or failure) before navigation
      const backendService = new BaseBackendService();
      const clickedUrl = window.location.href; // Use original URL with tracking params

      backendService
        .trackEmailClick(fromEmail || '', clickedUrl, {
          linkId: linkId || null,
          linkURL: linkURL || null,
        })
        .then((response) => {
          // Tracking request completed successfully - now safe to navigate
          // Use redirect URL from backend if provided, otherwise use cleaned URL
          if (response.redirectUrl) {
            // Use window.location.href for redirect from header
            window.location.href = response.redirectUrl;
            return;
          }
          if (response.requiresBrowserRedirect) {
            submitTrackClickForm({
              actionLogId: fromEmail || '',
              clickedUrl,
              linkId: linkId || null,
              linkURL: linkURL || null,
            });
            return;
          }
          // Navigate to cleaned URL - normal auth flow will handle login requirements
          navigate(newUrl, { replace: true });
          setShouldBlockRender(false);
        })
        .catch((error) => {
          // Tracking request completed with error - still safe to navigate
          // Silently fail - don't break user experience, but still redirect
          // Normal auth flow will handle login requirements if needed
          console.error('Failed to track email click:', error);
          navigate(newUrl, { replace: true });
          setShouldBlockRender(false);
        });
    } else if (!hasTrackingParams && hasProcessedUrl.current) {
      // Reset the processed URL tracker if we're on a different URL (without tracking params)
      hasProcessedUrl.current = null;
      setShouldBlockRender(false);
    }
  }, [
    hasTrackingParams,
    urlParams,
    fromEmail,
    linkId,
    linkURL,
    currentUrlKey,
    location.pathname,
    location.search,
    location.hash,
    isAdmin,
    navigate,
  ]);

  // Block rendering children until URL is cleaned (if we're processing tracking)
  if (shouldBlockRender) {
    return null;
  }

  // Render children normally (either no tracking params, or already processed, or waiting for auth)
  return <Outlet />;
};

/**
 * Component that tracks email link clicks on link navigation
 * - Intercepts all link clicks to check for email tracking params
 * - Strips tracking parameters from URL after tracking
 */
const EmailClickTracker: React.FC = () => {
  const navigate = useNavigate();

  // Intercept all link clicks to check for email tracking params
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a');

      if (!link || !link.href) {
        return;
      }

      try {
        const url = new URL(link.href, window.location.origin);
        const fromEmail = url.searchParams.get('fromEmail');
        const linkId = url.searchParams.get('linkId');
        const linkURL = url.searchParams.get('linkURL');

        // Only intercept if it's a same-origin link with email tracking params
        const hasTrackingParams = fromEmail || linkId || linkURL;
        if (
          hasTrackingParams &&
          url.origin === window.location.origin &&
          !link.hasAttribute('data-email-tracked')
        ) {
          // Skip tracking for impersonation route - it handles its own flow
          if (url.pathname === '/impersonate') {
            // Just clean the URL and let the link work normally
            url.searchParams.delete('fromEmail');
            url.searchParams.delete('linkId');
            url.searchParams.delete('linkURL');
            const cleanUrl = url.pathname + url.search + url.hash;
            // Update the link href and let it navigate normally
            link.href = cleanUrl;
            return; // Don't prevent default, let the link work
          }

          // Prevent default navigation and stop propagation
          event.preventDefault();
          event.stopPropagation();

          // Mark link as tracked to avoid duplicate tracking
          link.setAttribute('data-email-tracked', 'true');

          const backendService = new BaseBackendService();
          const clickedUrl = link.href;

          // Strip tracking parameters for fallback URL
          url.searchParams.delete('fromEmail');
          url.searchParams.delete('linkId');
          url.searchParams.delete('linkURL');
          const fallbackUrl = url.pathname + url.search + url.hash;

          // CRITICAL: Must wait for tracking request to complete (success or failure) before navigation
          backendService
            .trackEmailClick(fromEmail || '', clickedUrl, {
              linkId: linkId || null,
              linkURL: linkURL || null,
            })
            .then((response) => {
              // Tracking request completed successfully - now safe to navigate
              // Use redirect URL from backend if provided, otherwise use cleaned URL
              if (response.redirectUrl) {
                // Use window.location.href for redirect from header
                window.location.href = response.redirectUrl;
                return;
              }
              if (response.requiresBrowserRedirect) {
                submitTrackClickForm({
                  actionLogId: fromEmail || '',
                  clickedUrl,
                  linkId: linkId || null,
                  linkURL: linkURL || null,
                });
                return;
              }
              navigate(fallbackUrl);
            })
            .catch((error) => {
              // Tracking request completed with error - still safe to navigate
              // If tracking fails, still navigate to the cleaned URL
              console.error('Failed to track email click:', error);
              navigate(fallbackUrl);
            });
        }
      } catch (error) {
        // If URL parsing fails, let the link work normally
        console.error('Error parsing link URL:', error);
      }
    };

    // Add click listener to document
    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [navigate]);

  // This component doesn't render anything
  return null;
};

const EmailClickTrackerWithAuthContext: React.FC = () => {
  const auth0Value = useAuth0();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (auth0Value) {
      setAuthContextOnBackendService(auth0Value);
      setReady(true);
    }
  }, [auth0Value, setReady]);

  if (!ready) {
    return null;
  }

  return (
    <Auth0Context.Provider value={auth0Value}>
      <EmailClickTracker />
    </Auth0Context.Provider>
  );
};

export default EmailClickTrackerWithAuthContext;
